import { useState } from 'react';
import { Question, PracticeSessionResult } from '@/types/exam';
import { calculateGrade, calculatePercentage, formatTime } from '@/lib/utils';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { RichContent } from '@/components/content/RichContent';
import { TextBlock } from '@/components/content/TextBlock';
import { ShareButton } from '@/components/ShareButton';
import { PostQuizChallenge } from './PostQuizChallenge';
import { Trophy, Clock, Target, Zap, Home, Eye, Check, X, BookOpen, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePracticeStore } from '@/store/usePracticeStore';
import { useGamificationStore } from '@/store/useGamificationStore';
import { useAuthStore } from '@/store/useAuthStore';
import { sessionsApi } from '@/services/api';
import { useEffect, useRef } from 'react';
import { getContentLocale } from '@/lib/contentLocale';
import { playSuccess, playFailure } from '@/lib/sounds';
import { trackEvent } from '@/lib/analytics';

interface ResultsSummaryProps {
  results: PracticeSessionResult[];
  questions: Question[];
  examId: string;
  categoryId?: string;
  examName?: string;
  totalTime: number;
  totalPoints: number;
  passingScore: number;
  onRestart: () => void;
  mode?: 'practice' | 'simulation';
  /** Session quittée avant la fin (simulation) : pas de récompense, ton bienveillant. */
  abandoned?: boolean;
}

const letters = ['A', 'B', 'C', 'D', 'E', 'F'];

export function ResultsSummary({
  results, questions, examId, categoryId, examName, totalTime, totalPoints, passingScore, onRestart, mode = 'practice', abandoned = false,
}: ResultsSummaryProps) {
  const navigate = useNavigate();
  const locale = getContentLocale();
  const { addCompletedSession } = usePracticeStore();
  const { addXP, incrementStreak, recordQuiz, getXPProgress, level, streak, newAchievements, syncFromServer } = useGamificationStore();
  const { isAuthenticated, ensureGuestSession } = useAuthStore();
  const savedRef = useRef(false);
  const explanationRef = useRef<HTMLDivElement>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);

  const correctCount = results.filter(r => r.isCorrect).length;
  const score = calculatePercentage(correctCount, results.length);
  const passed = score >= passingScore;
  const { grade, color } = calculateGrade(score);
  const avgTime = results.length > 0 ? Math.round(results.reduce((s, r) => s + r.timeSpent, 0) / results.length) : 0;

  useEffect(() => {
    if (savedRef.current) return;
    savedRef.current = true;

    addCompletedSession({
      examId,
      categoryId: categoryId || examId,
      score,
      correctCount,
      totalQuestions: results.length,
      totalTime,
      completedAt: new Date().toISOString(),
    });

    trackEvent('quiz_complete', {
      mode,
      exam_id: examId,
      exam_name: examName,
      category_id: categoryId,
      score,
      passed,
      correct_count: correctCount,
      total_questions: results.length,
      duration_seconds: totalTime,
    });

    // Un abandon ne rapporte aucun XP (ni côté serveur ni localement) : on ne
    // récompense pas le fait de quitter, et les stats restent propres.
    const baseXP = correctCount * 10;
    const bonusXP = score === 100 ? 50 : score >= 80 ? 20 : 0;
    const simulationBonus = mode === 'simulation' ? 30 : 0;
    const xp = abandoned ? 0 : baseXP + bonusXP + simulationBonus;
    setEarnedXP(xp);

    (async () => {
      const canRecordServerSide = isAuthenticated || (await ensureGuestSession());

      // Un abandon ne met à jour ni XP, ni streak, ni historique de quiz.
      const applyLocalGamification = () => {
        if (abandoned) return;
        addXP(xp);
        incrementStreak();
        recordQuiz(examId, score);
      };

      if (!canRecordServerSide) {
        applyLocalGamification();
        return;
      }

      const recordPromise = mode === 'simulation'
        ? sessionsApi.recordSimulation({
            examId,
            score,
            correctCount,
            totalQuestions: results.length,
            timeSpent: totalTime,
            xpEarned: xp,
            abandoned,
          })
        : sessionsApi.recordPractice({
            examId,
            categoryId: categoryId!,
            score,
            correctCount,
            totalQuestions: results.length,
            timeSpent: totalTime,
            xpEarned: xp,
          });

      try {
        const { data } = await recordPromise;
        if (data.gamification) {
          syncFromServer({
            totalXp: data.gamification.totalXp,
            level: data.gamification.level,
            currentStreak: data.gamification.currentStreak,
          });
        }
        if (!abandoned) recordQuiz(examId, score);
      } catch {
        applyLocalGamification();
      }
    })();
  }, []);

  const [showConfetti, setShowConfetti] = useState(false);
  const soundPlayedRef = useRef(false);

  useEffect(() => {
    if (soundPlayedRef.current) return;
    soundPlayedRef.current = true;
    const timer = setTimeout(() => {
      if (abandoned) return; // session interrompue : pas de son de succès/échec
      if (passed) {
        playSuccess();
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4000);
      } else {
        playFailure();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [passed]);

  const xpProgress = getXPProgress();

  const difficultyBreakdown = (['easy', 'medium', 'hard'] as const).map(d => {
    const qs = questions.filter(q => q.difficulty === d);
    const correct = qs.filter(q => results.find(r => r.questionId === q.id)?.isCorrect).length;
    return { difficulty: d, total: qs.length, correct };
  }).filter(d => d.total > 0);

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const t = locale === 'en' ? {
    correct: 'Correct',
    duration: 'Duration',
    avgQuestion: 'Avg/question',
    points: 'Points',
    passed: 'Passed!',
    failed: 'Not passed',
    keepGoing: 'Keep going — you\'re learning',
    keepGoingHint: 'Every attempt sharpens you. Review the answers and try again.',
    interrupted: 'Session paused',
    interruptedHint: 'No worries — resume whenever you\'re ready.',
    byDifficulty: 'By difficulty',
    reviewAnswers: 'Review answers',
    yourAnswer: 'Your answer',
    correctAnswer: 'Correct answer',
    explanation: 'Explanation',
    back: 'Back',
    backToResults: 'Back to results',
    nextQuestion: 'Next',
    prevQuestion: 'Previous',
    question: 'Question',
    showExplanation: 'Explanation',
    hideExplanation: 'Hide',
    reviewTitle: 'Answer Review',
  } : {
    correct: 'Correctes',
    duration: 'Durée',
    avgQuestion: 'Moy/question',
    points: 'Points',
    passed: 'Réussi !',
    failed: 'Non réussi',
    keepGoing: 'Continue — tu progresses',
    keepGoingHint: 'Chaque essai te fait progresser. Revois les réponses et réessaie.',
    interrupted: 'Session interrompue',
    interruptedHint: 'Pas de souci — reprends quand tu veux.',
    byDifficulty: 'Par difficulté',
    reviewAnswers: 'Revoir les réponses',
    yourAnswer: 'Votre réponse',
    correctAnswer: 'Bonne réponse',
    explanation: 'Explication',
    back: 'Retour',
    backToResults: 'Retour aux résultats',
    nextQuestion: 'Suivante',
    prevQuestion: 'Précédente',
    question: 'Question',
    showExplanation: 'Explication',
    hideExplanation: 'Masquer',
    reviewTitle: 'Revue des réponses',
  };

  // --- REVIEW MODE: full exam-like experience ---
  if (reviewMode) {
    const question = questions[reviewIndex];
    const result = results.find(r => r.questionId === question.id);
    if (!question || !result) return null;

    const isFirst = reviewIndex === 0;
    const isLast = reviewIndex >= questions.length - 1;

    const handleNext = () => {
      setShowExplanation(false);
      if (!isLast) setReviewIndex(reviewIndex + 1);
    };
    const handlePrev = () => {
      setShowExplanation(false);
      if (!isFirst) setReviewIndex(reviewIndex - 1);
    };
    const handleBackToResults = () => {
      setReviewMode(false);
      setShowExplanation(false);
    };

    return (
      <div className="fixed inset-0 z-40 flex flex-col bg-app">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-line">
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
            <button
              onClick={handleBackToResults}
              className="flex items-center gap-1.5 text-fg-subtle hover:text-fg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-medium">{t.backToResults}</span>
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                result.isCorrect ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
              }`}>
                {result.isCorrect ? '✓' : '✗'} {result.isCorrect ? (locale === 'en' ? 'Correct' : 'Correct') : (locale === 'en' ? 'Incorrect' : 'Incorrect')}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="px-4 sm:px-6 pb-3">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs text-fg-subtle tabular-nums">
                {t.question} {reviewIndex + 1}/{questions.length}
              </span>
              <div className="flex-1 h-1 bg-overlay-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-purple-400 rounded-full transition-all duration-300"
                  style={{ width: `${((reviewIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Question navigator mini dots */}
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {questions.map((q, i) => {
                const r = results.find(res => res.questionId === q.id);
                const isCurrent = i === reviewIndex;
                return (
                  <button
                    key={q.id}
                    onClick={() => { setReviewIndex(i); setShowExplanation(false); }}
                    className={`w-7 h-7 flex-shrink-0 rounded-lg text-[10px] font-bold transition-all duration-200 ${
                      isCurrent
                        ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30 scale-110'
                        : r?.isCorrect
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Question content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
            {/* Question card */}
            <div className="bg-surface rounded-2xl border border-line p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs sm:text-sm font-medium text-fg-subtle">
                  {t.question} {reviewIndex + 1}/{questions.length}
                </span>
                <div className="flex items-center gap-2">
                  <DifficultyBadge difficulty={question.difficulty} size="sm" />
                  <span className="text-[10px] sm:text-xs text-fg-faint border border-line px-1.5 py-0.5 rounded">
                    {question.points} pt{question.points > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <RichContent content={question.content} />
            </div>

            {/* Options — exam style with green/red highlighting */}
            <div className="space-y-2 sm:space-y-3">
              {question.options.map((option, optIdx) => {
                const isSelected = result.selectedAnswer === optIdx;
                const isCorrectOpt = result.correctAnswer === optIdx;
                const isWrong = isSelected && !isCorrectOpt;

                let stateClasses = 'bg-overlay-1 border-line opacity-50';
                let letterClasses = 'bg-overlay-2 text-fg-faint';
                let indicator = null;
                let badge = null;

                if (isCorrectOpt) {
                  stateClasses = 'bg-green-500/15 border-green-500/40';
                  letterClasses = 'bg-green-500/20 text-green-400';
                  indicator = <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />;
                  if (isSelected) {
                    badge = <span className="text-[10px] font-medium text-green-400/70 flex-shrink-0">{t.yourAnswer} ✓</span>;
                  } else {
                    badge = <span className="text-[10px] font-medium text-green-400/70 flex-shrink-0">{t.correctAnswer}</span>;
                  }
                } else if (isWrong) {
                  stateClasses = 'bg-red-500/15 border-red-500/40';
                  letterClasses = 'bg-red-500/20 text-red-400';
                  indicator = <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 flex-shrink-0" />;
                  badge = <span className="text-[10px] font-medium text-red-400/70 flex-shrink-0">{t.yourAnswer}</span>;
                }

                return (
                  <div
                    key={optIdx}
                    className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border transition-all duration-200 ${stateClasses}`}
                  >
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 ${letterClasses}`}>
                      {letters[optIdx]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <RichContent content={option.content} inline={option.content.length === 1} />
                    </div>
                    {badge}
                    {indicator}
                  </div>
                );
              })}
            </div>

            {/* Explanation panel — shown when toggled */}
            {showExplanation && question.explanation && (
              <div ref={explanationRef} className="bg-blue-500/5 border border-blue-500/15 rounded-2xl p-4 sm:p-5 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-semibold text-blue-400">{t.explanation}</span>
                </div>
                <TextBlock text={question.explanation} className="text-sm text-fg-muted" />
              </div>
            )}
          </div>
        </div>

        {/* Footer navigation */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-t border-line bg-app/95 backdrop-blur-sm pb-20 lg:pb-3">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={isFirst}
              className="flex items-center gap-1 px-4 py-2.5 bg-overlay-1 border border-line rounded-xl text-sm text-fg-subtle hover:bg-overlay-2 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t.prevQuestion}</span>
            </button>

            {/* Explanation toggle button */}
            {question.explanation ? (
              <button
                onClick={() => {
                  const next = !showExplanation;
                  setShowExplanation(next);
                  if (next) {
                    setTimeout(() => explanationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                  }
                }}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  showExplanation
                    ? 'bg-blue-500/15 border border-blue-500/25 text-blue-400'
                    : 'bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">{showExplanation ? t.hideExplanation : t.showExplanation}</span>
              </button>
            ) : (
              <div />
            )}

            {isLast ? (
              <button
                onClick={handleBackToResults}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold text-sm hover:from-violet-700 hover:to-purple-700 transition-all active:scale-[0.98]"
              >
                {t.backToResults}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-sm hover:from-green-700 hover:to-emerald-700 transition-all active:scale-[0.98]"
              >
                {t.nextQuestion}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- RESULTS SUMMARY VIEW ---
  return (
    <div className="py-6 sm:py-8 px-4 animate-in fade-in duration-500 relative overflow-hidden">
      {/* Confetti overlay */}
      {showConfetti && <ConfettiOverlay />}

      <div className="max-w-5xl mx-auto">
        <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-6">
          {/* Left column */}
          <div>
            {/* Score hero */}
            <div className={`flex flex-col sm:flex-row items-center gap-6 mb-6 rounded-2xl border p-6 transition-all duration-700 ${
              passed
                ? 'bg-surface border-green-500/20 shadow-[0_0_40px_-10px_rgba(34,197,94,0.15)]'
                : 'bg-surface border-line'
            }`}>
              <div className="relative w-36 h-36 sm:w-40 sm:h-40 flex-shrink-0 animate-score-pop">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                  <circle
                    cx="60" cy="60" r="54" fill="none"
                    stroke={passed ? '#22c55e' : '#ef4444'}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl sm:text-4xl font-bold text-fg">{score}%</span>
                  <span className={`text-sm font-bold ${color}`}>{grade}</span>
                </div>
              </div>

              <div className="flex-1 text-center sm:text-left">
                <div className={`inline-flex px-4 py-1.5 rounded-full text-sm font-semibold mb-2 ${
                  abandoned
                    ? 'bg-overlay-2 text-fg-subtle'
                    : passed
                      ? 'bg-green-500/15 text-green-400 shadow-[0_0_20px_-5px_rgba(34,197,94,0.3)]'
                      : 'bg-amber-500/15 text-amber-400'
                }`}>
                  {abandoned ? `⏸ ${t.interrupted}` : passed ? `🎉 ${t.passed}` : `💪 ${t.keepGoing}`}
                </div>
                {/* Message bienveillant : un score bas ou un abandon ne doit pas
                    décourager — on oriente vers la progression, pas l'échec. */}
                {(abandoned || !passed) && (
                  <p className="text-xs text-fg-faint mb-2 leading-relaxed">
                    {abandoned ? t.interruptedHint : t.keepGoingHint}
                  </p>
                )}
                {examName && (
                  <p className="text-sm text-fg-subtle mb-4 line-clamp-2">{examName}</p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <MiniStat icon={<Target className="w-3.5 h-3.5 text-blue-400" />} value={`${correctCount}/${results.length}`} label={t.correct} />
                  <MiniStat icon={<Clock className="w-3.5 h-3.5 text-green-400" />} value={formatTime(totalTime)} label={t.duration} />
                  <MiniStat icon={<Zap className="w-3.5 h-3.5 text-yellow-400" />} value={`${avgTime}s`} label={t.avgQuestion} />
                  <MiniStat icon={<Trophy className="w-3.5 h-3.5 text-purple-400" />} value={`${totalPoints}`} label={t.points} />
                </div>
              </div>
            </div>

            {/* Difficulty Breakdown */}
            {difficultyBreakdown.length > 0 && (
              <div className="mb-6 bg-surface rounded-2xl border border-line p-5">
                <p className="text-xs font-semibold text-fg-subtle uppercase tracking-wider mb-3">{t.byDifficulty}</p>
                <div className="space-y-2.5">
                  {difficultyBreakdown.map(({ difficulty, total, correct }) => {
                    const pct = calculatePercentage(correct, total);
                    return (
                      <div key={difficulty} className="flex items-center gap-3">
                        <DifficultyBadge difficulty={difficulty} size="sm" />
                        <div className="flex-1">
                          <div className="h-2 bg-overlay-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-700 ${
                                pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-fg-subtle font-mono w-10 text-right">{correct}/{total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Review Answers Button — launches full review mode */}
            <div className="mb-6">
              <button
                onClick={() => { setReviewMode(true); setReviewIndex(0); setShowExplanation(false); }}
                className="w-full flex items-center justify-between p-4 rounded-2xl border bg-violet-500/10 border-violet-500/20 hover:bg-violet-500/15 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-500/20">
                    <Eye className="w-4.5 h-4.5 text-violet-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-violet-400">{t.reviewAnswers}</p>
                    <p className="text-[11px] text-fg-faint">
                      {correctCount}/{results.length} {t.correct.toLowerCase()} · {results.length - correctCount} {locale === 'en' ? 'incorrect' : 'incorrecte(s)'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-violet-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Actions — mobile */}
            <div className="flex gap-3 lg:hidden mb-6">
              {examName && (
                <ShareButton mode="score" examName={examName} examId={examId} score={score} className="flex-1 justify-center py-3" />
              )}
              <button
                onClick={() => navigate(`/exam/${examId}`)}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-overlay-1 border border-line rounded-xl text-sm font-medium text-fg-subtle hover:bg-overlay-2 transition-colors"
              >
                <Home className="w-4 h-4" />
                {t.back}
              </button>
            </div>
          </div>

          {/* Right column — sidebar (desktop) */}
          <div className="hidden lg:flex flex-col gap-4">
            <PostQuizChallenge
              examId={examId}
              categoryId={categoryId}
              score={score}
              streak={streak}
              xpGained={earnedXP}
              xpProgress={xpProgress}
              level={level}
              newAchievements={newAchievements}
              onRestart={onRestart}
            />
            <div className="flex flex-col gap-2 mt-2">
              {examName && (
                <ShareButton mode="score" examName={examName} examId={examId} score={score} className="justify-center py-3" />
              )}
              <button
                onClick={() => navigate(`/exam/${examId}`)}
                className="flex items-center justify-center gap-2 py-3 bg-overlay-1 border border-line rounded-xl text-sm font-medium text-fg-subtle hover:bg-overlay-2 transition-colors"
              >
                <Home className="w-4 h-4" />
                {t.back}
              </button>
            </div>
          </div>
        </div>

        {/* Gamification — mobile */}
        <div className="lg:hidden max-w-lg mx-auto">
          <PostQuizChallenge
            examId={examId}
            categoryId={categoryId}
            score={score}
            streak={streak}
            xpGained={earnedXP}
            xpProgress={xpProgress}
            level={level}
            newAchievements={newAchievements}
            onRestart={onRestart}
          />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-2.5 bg-overlay-1 rounded-lg p-2.5">
      {icon}
      <div>
        <p className="text-sm font-bold text-fg leading-none">{value}</p>
        <p className="text-[10px] text-fg-faint mt-0.5">{label}</p>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ['#22c55e', '#3b82f6', '#eab308', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];

function ConfettiOverlay() {
  const pieces = useRef(
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 4 + Math.random() * 6,
      shape: Math.random() > 0.5 ? 'circle' : 'rect',
    }))
  ).current;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {pieces.map((p) => (
        <div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.left}%`,
            top: '-10px',
            width: p.shape === 'circle' ? p.size : p.size * 0.6,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '1px',
            animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
            opacity: 0.9,
          }}
        />
      ))}
    </div>
  );
}
